/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnInit } from '@angular/core';
import { jwtDecode, PlatformService } from '@panamax/app-state';
import { ModalController } from '@ionic/angular';
import { Crumb } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import {
  debounceTime,
  filter,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  OrderConfirmationVM,
  SplittedOrder,
} from '../../models/order-confirmation-view-model';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { OrderService } from '../../services/order.service';
import {
  Order,
  OrderStatus,
  OrderHeader,
  OrderItem,
  MyOrdersStoreService,
} from '@usf/ngrx-order';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ViewDidEnter } from '@ionic/angular';
import { PanAppState } from '@panamax/app-state';
import { PageTitle } from 'src/app/shared/constants/page-titles.enum';
import { AppService } from 'src/app/shared/services/app.service';
import { ProductService } from 'src/app/shared/services/product/product.service';
import { NavigationHelperService } from 'src/app/shared/helpers/navigation.helpers.service';
import { OrderExceptionService } from '@order/pages/order-exceptions/service/order-exception.service';
import { OrderConfirmationTracingService } from './tracking/tracing/order-confirmation-tracing.service';
import { PATHS } from '../../../shared/constants/paths';
import { DealsService } from 'src/app/deals/services/deals.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../shared/services/modal/modal.service';
import { OrderDownloadService } from '@shared/services/file-download/order-download.service';
import { Product } from '@shared/models/product.model';
import { OrderSyncingComponent } from '@shared/components/popovers/order-syncing/order-syncing.component';
import { OrderConfirmationService } from './services/order-confirmation.service';
import { OrderPunchoutService } from 'src/app/order/services/order-punchout.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { isProntoDay } from '@shared/helpers/pronto.helpers';
import { OrderConversionService } from '@order/services/order-conversion.service';
import { Customer } from '@usf/customer-types';
import { UserReviewService } from '@shared/services/reviews/user-review.service';
import { PRONTO_KEYS } from '@shared/constants/pronto-constants';
@Component({
  selector: 'app-order-confirmation',
  templateUrl: './order-confirmation.page.html',
  styleUrls: ['./order-confirmation.page.scss'],
})
export class OrderConfirmationPage implements OnInit, ViewDidEnter {
  static MAX_CUSTOMER_ORDERS_API_CALLS = 4;
  static CUSTOMER_ORDERS_API_RETRY_INTERVAL = 5_000; //5 seconds
  static MY_ORDERS_PAGE_REDIRECT_TIME = 30_000; //30 seconds

  readonly orderStatusEnum = OrderStatus;

  // Component Properties
  breadcrumbs: Crumb[];
  VM: OrderConfirmationVM;
  masterOrderId: string;
  expandedShipments = new Set<string>();
  isDealsAvailable: boolean;
  confirmModalOpen = false;
  hasOnlyVSItems: boolean;
  firstSplitOrderReceived: boolean;
  sessionOrderTrace: [OrderHeader | any, boolean, boolean, boolean] = [
    null,
    false,
    false,
    false,
  ];
  superUserShipToCustomer$: Observable<Customer>;
  superUserShipToCustomer: Customer;
  hasSeparateShipToCustomer = false;
  isVptOrder: boolean;
  private toastDisplayed = false;

  // Subscriptions
  orderConfirmationVMSub$: Subscription;
  dealsProductsExistSub$: Subscription;
  isPartnerModifyingOrderSub$: Subscription;

  //Websocket timers
  customerOrdersApiIntervalRef: any;
  redirectTimerRef: any;

  showDownloadOrderModal$ = new BehaviorSubject<boolean>(false);

  showOrderConfirmationActionSheet$ = new BehaviorSubject<boolean>(false);

  refreshDataCount = 0;

  expectedShipments: number;

  isProntoDeliveryDate: boolean;
  isSuperUserProntoCustomer: boolean;

  hasUpdatedShipToCustomer = false;

  private isPunchoutSessionSubject = new BehaviorSubject<boolean>(false);
  isPunchoutSession$ = this.isPunchoutSessionSubject.asObservable();
  hasSubmittedWithExceptions = false;

  constructor(
    private modalController: ModalController,
    readonly platformService: PlatformService,
    private panAppState: PanAppState,
    readonly orderService: OrderService,
    readonly router: Router,
    private route: ActivatedRoute,
    private appService: AppService,
    private productService: ProductService,
    private navigationHelperService: NavigationHelperService,
    readonly orderExceptionService: OrderExceptionService,
    private orderConfirmationTracingService: OrderConfirmationTracingService,
    private dealsService: DealsService,
    private modalService: ModalService,
    private translate: TranslateService,
    private orderDownloadService: OrderDownloadService,
    private orderConfirmationService: OrderConfirmationService,
    private myOrdersStoreService: MyOrdersStoreService,
    private orderPunchoutService: OrderPunchoutService,
    private translateService: TranslateService,
    private toastService: ToastService,
    private orderConversionService: OrderConversionService,
    private userReviewService: UserReviewService,
  ) {}

  ngOnInit() {
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.myOrders',
        url: '/desktop/my-orders-view',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.orderSubmitted',
        url: '',
        active: true,
      },
    ];
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.orderSubmitted),
    );
  }

  ionViewWillEnter(): void {
    this.expectedShipments =
      Number(sessionStorage.getItem('submitOrderExpectedShipments')) ||
      undefined;

    this.firstSplitOrderReceived = false;
    this.masterOrderId = this.route.snapshot.params.orderId;
    this.punchOutSessionValidation();
    const orderConfirmationVm$ =
      this.orderService.loadOrderConfirmationViewModel$(
        this.masterOrderId,
        true,
      );

    this.orderConfirmationVMSub$ = orderConfirmationVm$
      .pipe(
        debounceTime(250),
        filter(vm => vm?.ready),
        withLatestFrom(this.panAppState.previousUrl$),
      )
      .subscribe(model => {
        this.VM = model[0];
        this.hasSeparateShipToCustomer =
          this.VM?.masterOrderById?.orderHeader?.shipToCustomer * 1 !==
          this.VM?.customerProfile?.customerNumber * 1;
        this.getSuperUserShipToCustomerIfNeeded(this.VM);

        this.expectedShipments =
          this.expectedShipments ?? this.VM.expectedShipmentCount;
        this.hasSubmittedWithExceptions = this.VM?.splittedOrders?.some(
          split =>
            split?.order?.orderHeader?.orderStatus ===
            OrderStatus.SUBMITTED_WITH_EXCEPTIONS,
        );
        this.presentTandemRejectToastMsg(this.VM?.masterOrderById);
        this.checkForPunchoutAutotransfer();
        this.checkForProntoOrder();

        const previousUrl = model[1];
        const orderExists = !!this.VM?.masterOrderById?.orderItems;
        const orderCancelled =
          this.VM?.masterOrderById?.orderHeader?.orderStatus ===
            OrderStatus.CANCELLING ||
          this.VM?.masterOrderById?.orderHeader?.orderStatus ===
            OrderStatus.CANCELLED;
        if ((!orderExists && !previousUrl) || orderCancelled) {
          clearInterval(this.customerOrdersApiIntervalRef);
          clearInterval(this.redirectTimerRef);
          this.routeTo(PATHS.MY_ORDERS_VIEW);
          return;
        }
        if (
          !this.firstSplitOrderReceived &&
          this.VM.splittedOrders.length > 0
        ) {
          this.userReviewService.initiateAppReviewFlow(this.VM);
          this.isVptOrder = !!sessionStorage.getItem('submitVPTOrderTrace');

          this.firstSplitOrderReceived = true;
          const [sessionOrder, isWebSocket, traceDispatched, isEmpty] =
            this.sessionOrderTrace;

          if (
            (this.refreshDataCount > 0 ||
              (isWebSocket && !isEmpty) ||
              traceDispatched) &&
            !this.VM.splittedOrders[0].order.orderHeader.editOrderInFlight
          ) {
            !this.isVptOrder
              ? this.orderConfirmationTracingService.endOrderSubmissionTrace(
                  this.VM.splittedOrders[0].order,
                  isWebSocket,
                )
              : this.orderConfirmationTracingService.endVptOrderSubmissionTrace(
                  this.VM.splittedOrders[0].order,
                  isWebSocket,
                );
          }
        }
        if (
          this.expectedShipments === 1 &&
          this.VM.splittedOrders.length === 1 &&
          this.VM.splittedOrders[0].order.orderHeader.orderStatus ===
            OrderStatus.SUBMITTED_WITH_EXCEPTIONS &&
          !this.VM.splittedOrders[0].order.orderHeader.editOrderInFlight &&
          (!this.VM?.navigationRestricted || this.VM?.exceptionRestricted)
        ) {
          if (this.VM?.partnerAutotransfer) {
            this.orderPunchoutService.dismissSpinnerModal();
          }

          this.orderExceptionService.navigateToResolveExceptionsPage(
            this.VM.splittedOrders[0].order.orderHeader,
            true,
          );
          return;
        } else if (
          this.hasSubmittedWithExceptions &&
          this.expectedShipments > 1 &&
          this.VM?.splittedOrders?.length > 1 &&
          !this.VM?.splittedOrders[0]?.order?.orderHeader?.editOrderInFlight &&
          (!this.VM?.navigationRestricted || this.VM?.exceptionRestricted)
        ) {
          // If there are more than 1 splitted orders and if RT order has exceptions, navigate to exceptions page
          // If only VT order has exceptions, stay on current page (do nothing)
          // If neither has exceptions, do nothing

          // Find RT-exception order from splitted orders on confirmation page
          const rtOrderWithException = this.VM.splittedOrders.find(
            split =>
              split.order.orderHeader.orderType === 'RT' &&
              split.order.orderHeader.orderStatus ===
                OrderStatus.SUBMITTED_WITH_EXCEPTIONS,
          );
          // If RT order has exceptions, navigate to exceptions page
          if (rtOrderWithException) {
            if (this.VM?.partnerAutotransfer) {
              this.orderPunchoutService.dismissSpinnerModal();
            }

            this.orderExceptionService.navigateToResolveExceptionsPage(
              rtOrderWithException.order.orderHeader,
              true,
            );
            return;
          }
        }
        if (orderExists) {
          //if there is only 'VS'-orderItems -> orderType should be 'VS'
          this.hasOnlyVSItems =
            this.VM?.masterOrderById?.orderItems?.filter(
              oi => !oi.specialVendor,
            )?.length === 0;
          //get products from orderItems
          const productNumbers = this.VM?.masterOrderById?.orderItems?.map(
            item => +item.productNumber,
          );
          this.productService.loadProducts(productNumbers);
        }
      });
    if (!!sessionStorage.getItem('isPartnerModifyingOrder')) {
      this.isPartnerModifyingOrderSub$ = this.router.events
        .pipe(
          filter(
            (event: NavigationEnd) =>
              event instanceof NavigationEnd &&
              !event.url.includes('order-exceptions'),
          ),
          take(1),
        )
        .subscribe(() => {
          sessionStorage.removeItem('isPartnerModifyingOrder');
        });
    }
    /**
     * Handling an edge case where split orders are either empty or partial after 10 seconds of websocket call wait.
     * In that case, we call customerOrders api if expected shipment counts does not match with actual websocket arrival counts.
     */
    // Only start another30 sec redirect timer if not in punchout session
    if (!this.isPunchoutSessionSubject.value) {
      this.startRedirectTimer(); //normal scenario
    } else {
      //AT this point we are still waiting for an order to comeback
      //start another 30 second redirect timer if in punchout session (total 60 seconds wait before navigating to my-orders)
      //After first 30 seconds we  close IP order auto transfer modal and open another one with failed and retry text
      //After total 60 seconds If we dont get an order back from either customers orders api or websocket, we navigate to my-orders page and keep modal open  for blocking purpose.
      //User must read their failedstatus of transfer and close browser
      if (!!this.orderPunchoutService?.loadingSpinnerModalWMessage) {
        this.startPunchoutTransferTimer();
      }
    }
    this.startCustomerOrdersAPITimer();
    this.calculateIfDealsAreAvailable();
  }

  ionViewDidEnter(): void {
    // Open Usabilla Feedback Form
    this.appService.getCustomFeedback('order_submitted');

    const vm$ = this.orderService.loadOrderConfirmationViewModel$(
      this.masterOrderId,
      true,
    );

    vm$
      .pipe(
        filter(vm => !!vm?.ready && !!vm.masterOrderById?.orderItems),
        take(1),
      )
      .subscribe(vm => {
        this.orderConfirmationTracingService.tracePageLoad(
          vm.masterOrderById.orderHeader.origOrderId,
          Math.round(100 * vm.orderTallies?.totalPrice) / 100,
        );
      });
  }

  startCustomerOrdersAPITimer(): void {
    this.refreshDataCount = 0;
    this.customerOrdersApiIntervalRef = setInterval(() => {
      this.sessionOrderTrace = !this.isVptOrder
        ? JSON.parse(sessionStorage.getItem('submitOrderTrace')) || [
            null,
            false,
            false,
            false,
          ]
        : JSON.parse(sessionStorage.getItem('submitVPTOrderTrace')) || [
            null,
            false,
            false,
            false,
          ];
      let [sessionOrder, isWebSocket, traceDispatched, isEmpty] =
        this.sessionOrderTrace;
      this.refreshDataCount++;
      if (
        !!sessionOrder &&
        this.masterOrderId?.indexOf(sessionOrder['orderId']) === -1
      ) {
        !this.isVptOrder
          ? sessionStorage.removeItem('submitOrderTrace')
          : sessionStorage.removeItem('submitVPTOrderTrace');
        sessionOrder = null;
        isWebSocket = false;
        traceDispatched = false;
      }

      const arrivingWebsocketSplitCount = this.VM?.splittedOrders?.filter(
        split =>
          (split?.order?.orderHeader?.orderStatus === OrderStatus.SUBMITTED ||
            split.order?.orderHeader?.orderStatus ===
              OrderStatus.SUBMITTED_WITH_EXCEPTIONS ||
            split?.order?.orderHeader?.orderStatus ===
              OrderStatus.SUBMITTED_CREDIT_HOLD ||
            split.order?.orderHeader?.orderStatus ===
              OrderStatus.ORDER_ERROR) &&
          !split.order?.orderHeader?.editOrderInFlight,
      ).length;

      if (isEmpty && arrivingWebsocketSplitCount === 0) {
        // Restart timer after websocket response is empty
        sessionStorage.setItem(
          'submitOrderTrace',
          JSON.stringify([sessionOrder, isWebSocket, traceDispatched, false]),
        );
        clearInterval(this.customerOrdersApiIntervalRef);
        clearTimeout(this.redirectTimerRef);
        this.startCustomerOrdersAPITimer();
      } else if (
        this.refreshDataCount >
        OrderConfirmationPage.MAX_CUSTOMER_ORDERS_API_CALLS
      ) {
        clearInterval(this.customerOrdersApiIntervalRef);
      } else if (this.expectedShipments > arrivingWebsocketSplitCount) {
        this.myOrdersStoreService.retrieveCustomerOrders();
      } else {
        clearInterval(this.customerOrdersApiIntervalRef);
      }
    }, OrderConfirmationPage.CUSTOMER_ORDERS_API_RETRY_INTERVAL);
  }

  startRedirectTimer(): void {
    this.redirectTimerRef = setTimeout(() => {
      const splitOrderExist = this.VM?.splittedOrders.length > 0;
      const shouldRedirectToMyOrders =
        !splitOrderExist ||
        this.VM?.splittedOrders[0]?.order?.orderHeader?.orderStatus ===
          OrderStatus.SUBMITTING ||
        this.VM?.splittedOrders[0]?.order?.orderHeader?.orderStatus ===
          OrderStatus.EDITING_SUBMITTED ||
        this.VM?.splittedOrders[0]?.order?.orderHeader?.editOrderInFlight ===
          true;
      if (shouldRedirectToMyOrders) {
        clearInterval(this.customerOrdersApiIntervalRef);
        this.routeTo(PATHS.MY_ORDERS_VIEW);
      }
    }, OrderConfirmationPage.MY_ORDERS_PAGE_REDIRECT_TIME);
  }

  calculateIfDealsAreAvailable() {
    this.dealsProductsExistSub$ = this.dealsService
      .selectDealsProductsExist$()
      .subscribe(dealsProductsExist => {
        this.isDealsAvailable = dealsProductsExist;
      });
  }

  checkForProntoOrder() {
    this.orderService
      .selectedCustomerProntoDeliveryDays()
      .pipe(take(1))
      .subscribe(selectedDays => {
        this.isProntoDeliveryDate = isProntoDay(
          selectedDays,
          this.VM?.masterOrderById?.orderHeader?.confirmedDeliveryDate?.toString(),
        );
      });
  }

  toggleShipmentProducts(orderId: string): void {
    if (this.expandedShipments.has(orderId)) {
      this.expandedShipments.delete(orderId);
    } else {
      this.expandedShipments.add(orderId);
    }
  }

  openOrder(order: Order) {
    const customerNumber = order?.orderHeader.customerNumber;
    const departmentNumber = order?.orderHeader.departmentNumber;
    const orderId = `${customerNumber}:${departmentNumber}:${order?.orderHeader.orderId}`;

    this.navigationHelperService.routeToSubmittedOrder(orderId);
  }

  ionViewWillLeave(): void {
    if (this.orderConfirmationVMSub$) {
      this.orderConfirmationVMSub$.unsubscribe();
    }
    if (this.dealsProductsExistSub$) {
      this.dealsProductsExistSub$.unsubscribe();
    }
    if (this.customerOrdersApiIntervalRef) {
      clearInterval(this.customerOrdersApiIntervalRef);
    }
    if (this.redirectTimerRef) {
      clearTimeout(this.redirectTimerRef);
    }
    if (this.isPartnerModifyingOrderSub$) {
      this.isPartnerModifyingOrderSub$.unsubscribe();
    }
    this.firstSplitOrderReceived = false;
  }

  routeTo(path: string) {
    if (!this.VM?.navigationRestricted) {
      const url = window.location.pathname;
      this.router.navigate([url.split('/')[1], path]);
    }
  }

  navigateToOrderExceptions(orderHeader: OrderHeader, orderItem: OrderItem[]) {
    if (!this.VM?.navigationRestricted || this.VM?.exceptionRestricted) {
      this.orderExceptionService.navigateToResolveExceptionsPage(
        orderHeader,
        true,
      );
    }
  }

  resubmitOrder(order: Order) {
    this.orderService
      .shouldShowCartSyncingPopover$()
      .pipe(take(1))
      .subscribe(async showPopover => {
        if (showPopover) {
          await this.presentSyncingModal(order);
        } else {
          this.routeTo(PATHS.ORDER);
          this.orderService.makeOrderCurrentOrder(order);
        }
      });
  }

  async presentSyncingModal(order: Order) {
    this.modalService.setModalOptions(
      this.platformService?.isTouch.value,
      OrderSyncingComponent,
      { customerSwitch: false, orderSwitch: true },
      'simple-action-modal',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    modal.onDidDismiss().then(resp => {
      if (resp?.data?.continueClick) {
        this.routeTo(PATHS.ORDER);
        this.orderService.makeOrderCurrentOrder(order);
      }
    });
    return await modal.present();
  }

  showOrderConfirmationActionSheet(show: boolean) {
    this.showOrderConfirmationActionSheet$.next(show);
  }

  constructDownloadOrderButtons() {
    let buttons = [];
    buttons.push({
      text: this.translate.instant('i18n.orderPage.print'),
      handler: () => {
        this.printOrder();
      },
    });

    buttons.push({
      text: this.translate.instant('i18n.orderPage.download'),
      handler: () => {
        this.showDownloadOrdersModal(true);
      },
    });

    buttons.push({
      text: this.translate.instant('i18n.orderPage.share'),
      handler: () => {
        this.shareOrder();
      },
    });

    buttons.push({
      text: this.translate.instant('i18n.common.cancel'),
      role: 'cancel',
      cssClass: 'cancel-button',
    });

    return buttons;
  }

  showDownloadOrdersModal(show: boolean) {
    this.showDownloadOrderModal$.next(show);
  }

  handleDownloadOrders({ fileName, fileType, orders }) {
    const products = new Map<number, Product>();

    this.VM?.splittedOrders.forEach(order => {
      order.cartProductsItems.forEach(item => {
        products.set(item.orderItem.productNumber, {
          contract: item.productContract,
          inventory: item.productInventory,
          summary: item.productState,
        } as Product);
      });
    });

    this.orderConfirmationService.downloadOrderConfirmationAnalytics(
      fileType?.fileExtension?.toLowerCase(),
      this.VM?.masterOrderById?.orderHeader?.orderId,
      orders?.length,
    );
    this.orderConfirmationTracingService.traceConfirmationOrderDownload(
      this.VM?.masterOrderById?.orderHeader,
      fileType,
    );
    this.orderDownloadService.downloadAndOpenOrder(orders, {
      products,
      fileName,
      fileType,
      multipleExports: true,
    });
  }

  getOrdersFromSplittedOrders(splittedOrders: SplittedOrder[]): Order[] {
    return splittedOrders.map(splitted => splitted.order);
  }

  public printOrder() {
    const shipToCustomer =
      this.VM?.shipToCustomer || this.superUserShipToCustomer;

    return this.orderService.printSubmittedOrder(
      this.VM?.splittedOrders?.map(splitted => splitted.order),
      true,
      this.VM?.orderTallies,
      null,
      shipToCustomer,
      this.VM?.shipToDepartmentName,
    );
  }
  isSubmitting(): boolean {
    return (
      this.VM?.splittedOrders.length === 0 ||
      (this.expectedShipments > this.VM?.splittedOrders?.length &&
        !!this.VM?.masterOrderById?.orderHeader?.origOrderId) ||
      this.VM?.splittedOrders[0]?.order?.orderHeader?.orderStatus ===
        this.orderStatusEnum.EDITING_SUBMITTED ||
      this.VM?.splittedOrders[0]?.order?.orderHeader?.orderStatus ===
        this.orderStatusEnum.IN_PROGRESS ||
      this.VM?.splittedOrders[0]?.order?.orderHeader?.orderStatus ===
        this.orderStatusEnum.SUBMITTING
    );
  }
  checkForPunchoutAutotransfer() {
    if (
      this.VM?.partnerAutotransfer &&
      this.VM?.splittedOrders.length >= this.expectedShipments
    ) {
      this.orderPunchoutService.dismissSpinnerModal();
      if (
        this.VM.splittedOrders.every(
          order =>
            order.order.orderHeader.orderStatus === OrderStatus.SUBMITTED ||
            order.order.orderHeader.orderStatus ===
              OrderStatus.SUBMITTED_CREDIT_HOLD,
        )
      ) {
        this.doPunchoutTransfer(false);
      }
    }
  }
  doPunchoutTransfer(buttonClicked = true) {
    const orderArray = this.VM?.splittedOrders.map(
      splitOrder => splitOrder.order,
    );
    if (buttonClicked) {
      this.orderPunchoutService.tracePunchoutTransferClick();
    }
    this.orderPunchoutService.doPunchoutTransfer(orderArray);
  }

  presentTandemRejectToastMsg(order: Order) {
    if (
      order?.orderHeader &&
      !!order?.orderHeader?.errorDetails &&
      order?.orderHeader?.errorDetails?.errNumber === 50004
    ) {
      if (this.toastDisplayed) {
        return;
      }
      this.toastDisplayed = true;
      const fullOrderId =
        `${order?.orderHeader?.customerNumber}:` +
        `${order?.orderHeader?.departmentNumber}:` +
        `${order?.orderHeader?.orderId}`;
      const platformType = this.platformService.platformType;
      const nonModifiableOrderMessage =
        platformType === 'mobile'
          ? this.translateService.instant(
              'i18n.orderPage.nonModifiableOrderMobileErrorMsg',
            )
          : this.translateService.instant(
              'i18n.orderPage.nonModifiableOrderDesktopErrorMsg',
            );

      this.toastService.presentToastMsg(
        nonModifiableOrderMessage,
        'red-toast',
        MessageTypeEnum.warning,
        this.toastService.setOrderErrorToastButtons(fullOrderId),
      );
      this.orderService.resetOrder();
    }
  }

  getSuperUserShipToCustomerIfNeeded(vm: OrderConfirmationVM): void {
    if (
      !this.VM?.shipToCustomer &&
      !!this.VM?.masterOrderById?.orderHeader?.shipToCustomer &&
      this.hasSeparateShipToCustomer === true &&
      !!this.VM?.customerProfile?.divisionNumber &&
      !this.hasUpdatedShipToCustomer
    ) {
      this.superUserShipToCustomer$ = this.orderConversionService
        .getCustomerForSuperUser$(
          vm?.masterOrderById?.orderHeader?.shipToCustomer * 1,
          vm?.customerProfile?.divisionNumber * 1, //ship to customer will always have same division as current customer
        )
        .pipe(
          tap(cust => {
            this.superUserShipToCustomer = cust;
            this.isSuperUserProntoCustomer =
              this.superUserShipToCustomer?.prontoEnabledFlag ===
              PRONTO_KEYS.PRONTO;
            if (!!this.VM?.masterOrderById?.orderHeader?.shipToDepartment) {
              this.VM.shipToDepartmentName = cust?.departments?.find(
                dept =>
                  Number(dept?.departmentNumber) ===
                  vm?.masterOrderById?.orderHeader?.shipToDepartment * 1,
              )?.departmentName;
            }
          }),
        );
      this.hasUpdatedShipToCustomer = true;
    }
  }
  shareOrder(): void {
    this.orderService.shareOrderConfirmation(
      this.getOrdersFromSplittedOrders(this.VM?.splittedOrders),
    );
  }
  isSuperUser$(): Observable<boolean> {
    return this.orderService.isSuperUser$();
  }
  punchOutSessionValidation() {
    this.panAppState.accessToken$
      .pipe(
        filter(token => !!token),
        take(1),
      )
      .subscribe(token => {
        const decoded = jwtDecode(token);
        if (decoded) {
          const punchoutSession = decoded['usf-claims']?.punchoutSession;
          this.isPunchoutSessionSubject.next(!!punchoutSession);
        }
      });
  }

  //First 30 seconds timer for punchout session auto transfer
  startPunchoutTransferTimer() {
    this.redirectTimerRef = setTimeout(() => {
      const splitOrderExist = this.VM?.splittedOrders.length > 0;
      const shouldRedirectToMyOrders =
        !splitOrderExist ||
        this.VM?.splittedOrders[0]?.order?.orderHeader?.orderStatus ===
          OrderStatus.SUBMITTING ||
        this.VM?.splittedOrders[0]?.order?.orderHeader?.orderStatus ===
          OrderStatus.EDITING_SUBMITTED ||
        this.VM?.splittedOrders[0]?.order?.orderHeader?.editOrderInFlight ===
          true;
      if (shouldRedirectToMyOrders && !!this.isPunchoutSessionSubject.value) {
        clearInterval(this.customerOrdersApiIntervalRef);
        this.handlePunchoutTransferFinalTimeout();
      }
    }, OrderConfirmationPage.MY_ORDERS_PAGE_REDIRECT_TIME);
  }

  //Another 30 seconds timer for punchout session auto transfer (Failed and retry text modal)
  handlePunchoutTransferFinalTimeout() {
    this.orderPunchoutService.createSpinnerWithMessageModal(true);

    this.redirectTimerRef = setTimeout(() => {
      const splitOrderExist = this.VM?.splittedOrders.length > 0;
      const shouldRedirectToMyOrders =
        !splitOrderExist ||
        this.VM?.splittedOrders[0]?.order?.orderHeader?.orderStatus ===
          OrderStatus.SUBMITTING ||
        this.VM?.splittedOrders[0]?.order?.orderHeader?.orderStatus ===
          OrderStatus.EDITING_SUBMITTED ||
        this.VM?.splittedOrders[0]?.order?.orderHeader?.editOrderInFlight ===
          true;
      if (shouldRedirectToMyOrders && !!this.isPunchoutSessionSubject.value) {
        clearInterval(this.customerOrdersApiIntervalRef);

        this.routeTo(PATHS.MY_ORDERS_VIEW);
      }
    }, OrderConfirmationPage.MY_ORDERS_PAGE_REDIRECT_TIME);
  }
}
